import React, { useEffect, useState } from "react";
import { subscribeConfirmDelete, resolveConfirmDelete } from "../lib/confirmDelete";

/**
 * Global "Are you sure?" modal mounted once at App level.
 * Driven by the Promise-based `confirmDelete()` helper — listens for
 * the next pending deletion and renders a centered card with
 *   • Cancel  (large, neutral — easy escape if tap was a mistake)
 *   • Delete  (red, smaller — destructive)
 *   • Don't ask again checkbox
 */
export default function DeleteConfirmDialog() {
  const [pending, setPending] = useState(null);
  const [skip, setSkip] = useState(false);

  useEffect(() => {
    // Reset checkbox each time a new confirmation opens so the choice
    // is intentional per-delete.
    return subscribeConfirmDelete((s) => {
      setPending(s);
      if (s) setSkip(false);
    });
  }, []);

  // Allow ESC to dismiss (web testing convenience — iOS has no
  // hardware Escape but doesn't hurt).
  useEffect(() => {
    if (!pending) return;
    function onKey(e) {
      if (e.key === "Escape") resolveConfirmDelete(false, false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending]);

  if (!pending) return null;

  const { opts } = pending;
  const title = opts.title || "Are you sure?";
  const subtext = opts.subtext || "Deleted items can't be undone.";
  const confirmLabel = opts.confirmLabel || "Delete";

  return (
    <div
      data-testid="delete-confirm-backdrop"
      onClick={() => resolveConfirmDelete(false, false)}
      className="fixed inset-0 z-[100] bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
    >
      <div
        data-testid="delete-confirm-dialog"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-navy/5 p-6 animate-pop-in"
      >
        <h2 className="font-jakarta font-extrabold text-2xl text-navy mb-1.5 text-center">
          {title}
        </h2>
        <p className="font-jakarta font-medium text-[13px] text-navy-soft text-center mb-5 leading-relaxed">
          {subtext}
        </p>

        <label className="flex items-center gap-2.5 mb-5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={skip}
            onChange={(e) => setSkip(e.target.checked)}
            data-testid="delete-confirm-dont-ask"
            className="w-5 h-5 accent-orange shrink-0"
          />
          <span className="font-jakarta font-semibold text-sm text-navy">
            Don&apos;t ask again
          </span>
        </label>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => resolveConfirmDelete(false, false)}
            data-testid="delete-confirm-cancel"
            className="flex-1 bg-white text-navy border-2 border-navy/15 rounded-full py-3 font-jakarta font-extrabold text-base active:scale-[0.98] transition-transform"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => resolveConfirmDelete(true, skip)}
            data-testid="delete-confirm-delete"
            className="flex-1 bg-red-600 text-white rounded-full py-3 font-jakarta font-extrabold text-base active:scale-[0.98] transition-transform"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
